import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useOktaAuth } from '@okta/okta-react';
import { AiUsageContext } from './AiUsageContext';
import { useTenant } from './TenantContext';
import useApi from '../hooks/useApi';
import { fetchAiUsage } from '../services/aiGovernanceService';
import { AI_USAGE_CHANGED_EVENT } from '../constants/aiUsage';

/**
 * Loads and keeps fresh the signed-in user's client AI token usage.
 *
 * Refresh triggers, in order of importance:
 *  1. tenant change / first load
 *  2. the `AI_USAGE_CHANGED_EVENT` the API layer fires after a successful AI run
 *     — this is why the eight widget components that run AI need no changes
 *  3. a slow interval, to catch usage from the user's other tabs and colleagues
 *
 * Failures are held locally and never surfaced as a toast: usage is ambient
 * information, and a metering blip must not interrupt the user's work.
 */
const REFRESH_INTERVAL_MS = 120000;
/** Collapse the burst of events a dashboard fires when it runs many cards at once. */
const EVENT_DEBOUNCE_MS = 1500;

export const AiUsageProvider = ({ children }) => {
  const api = useApi();
  const { authState } = useOktaAuth();
  const { currentTenant } = useTenant();
  const tenantId = currentTenant?.tenantId ?? null;
  const isAuthenticated = !!authState?.isAuthenticated;

  const [state, setState] = useState({
    summary: null,
    byModel: [],
    canViewCost: false,
    loading: false,
    error: null,
  });

  // Guards against setState after unmount and against overlapping loads
  const mountedRef = useRef(true);
  const inFlightRef = useRef(false);
  const debounceRef = useRef(null);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, []);

  const load = useCallback(async () => {
    if (!isAuthenticated || !tenantId || inFlightRef.current) {
      return;
    }

    inFlightRef.current = true;
    setState((prev) => ({ ...prev, loading: !prev.summary, error: null }));

    try {
      const { summary, byModel, canViewCost } = await fetchAiUsage(api);
      if (mountedRef.current) {
        setState({ summary, byModel, canViewCost, loading: false, error: null });
      }
    } catch (err) {
      // Ambient data: keep the last good values and stay quiet in the UI
      console.warn('Could not load AI token usage:', err?.message || err);
      if (mountedRef.current) {
        setState((prev) => ({ ...prev, loading: false, error: err?.message || 'Failed to load AI usage' }));
      }
    } finally {
      inFlightRef.current = false;
    }
  }, [api, isAuthenticated, tenantId]);

  // Tenant switch invalidates everything we hold
  useEffect(() => {
    if (!isAuthenticated || !tenantId) {
      setState({ summary: null, byModel: [], canViewCost: false, loading: false, error: null });
      return;
    }
    setState({ summary: null, byModel: [], canViewCost: false, loading: true, error: null });
    load();
  }, [isAuthenticated, tenantId, load]);

  // An AI call just completed somewhere in the app
  useEffect(() => {
    if (!isAuthenticated || !tenantId) return undefined;

    const handleUsageChanged = () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
      debounceRef.current = setTimeout(load, EVENT_DEBOUNCE_MS);
    };

    window.addEventListener(AI_USAGE_CHANGED_EVENT, handleUsageChanged);
    return () => window.removeEventListener(AI_USAGE_CHANGED_EVENT, handleUsageChanged);
  }, [isAuthenticated, tenantId, load]);

  // Catch usage generated outside this tab
  useEffect(() => {
    if (!isAuthenticated || !tenantId) return undefined;

    const interval = setInterval(() => {
      if (document.visibilityState === 'visible') {
        load();
      }
    }, REFRESH_INTERVAL_MS);

    return () => clearInterval(interval);
  }, [isAuthenticated, tenantId, load]);

  const value = useMemo(
    () => ({
      summary: state.summary,
      byModel: state.byModel,
      canViewCost: state.canViewCost,
      loading: state.loading,
      error: state.error,
      isConfigured: !!state.summary?.isConfigured,
      refresh: load,
    }),
    [state, load],
  );

  return <AiUsageContext.Provider value={value}>{children}</AiUsageContext.Provider>;
};

export default AiUsageProvider;
